import { Typography, alpha } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { GridInitialStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity"
import { getAdvertiserNameById } from "advertiser/tools/common"
import { translate } from "app/language/service"
import { getFormatedNumber } from "app/tools/number"
import { dateSVC } from "app/util/date/service"
import { CURRENCIES } from "currency/model"
import { getCurrencySymbol } from "form/inventory/tools/common"
import { IOrder, IOrderList } from "order/model"
import { closeOrderFormPopup, openEditOrderPopup } from "order/tools/form"
import { refreshOrderList } from "order/tools/list"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { ListIdCell } from "ui/components/ListIdCell"
import { getDataGridTexts } from "ui/tools/list"
import { canAccessDspDeal, canSaveOrder } from "user/tools/permissions"
import { OrderCampaignsCell } from "./OrderCampaignsCell"
import { OrderCampaignsPopup } from "./OrderCampaignsPopup"
import { OrderFormPopup } from "./OrderFormPopup"
import { OrderListAction } from "./OrderListActions"

interface IOrderListProps {
  isLoading: boolean
  isDspDeal: boolean
  list: IOrderList
  sx?: any
}

export const OrderList = React.memo(
  ({
    isLoading,
    list: { filteredOrders, hasRefreshError, isRefreshing, pagination, rowCount },
    isDspDeal,
    sx
  }: IOrderListProps): JSX.Element => {
    const [selectedOrder, setSelectedOrder] = React.useState<null | IOrder>(null)
    const userCanAccessDspDeal = canAccessDspDeal()
    // first render use effect
    React.useEffect(() => {
      refreshOrderList({})
    }, [])
    const canSave = canSaveOrder()
    const columns: Array<GridColDef<IOrder>> = [
      {
        field: "name",
        headerName: translate("label.name"),
        minWidth: 110,
        flex: 3,
        renderCell: params => (
          <ListIdCell
            label={params.row.name || "-"}
            id={params.row.id}
            description={params.row.notes}
            onClick={canSave ? () => openEditOrderPopup(params.row.id) : undefined}
          />
        )
      },
      {
        field: "advertiser_id",
        headerName: translate("label.advertiser"),
        minWidth: 110,
        flex: 3,
        renderCell: params => (
          <ListIdCell
            label={getAdvertiserNameById(params.row.advertiser_id || -1)}
            id={params.row.advertiser_id || -1}
          />
        )
      },
      {
        field: "campaign_count",
        headerName: translate("campaigns.title"),
        minWidth: 90,
        flex: 2,
        renderCell: params => <OrderCampaignsCell order={params.row} onClick={order => setSelectedOrder(order)} />
      },
      {
        field: "budget",
        headerName: translate("label.budget"),
        minWidth: 90,
        flex: 2,
        renderCell: params =>
          params.row.budget
            ? `${getFormatedNumber(params.row.budget)} ${getCurrencySymbol(
                params.row.budget_currency_id || CURRENCIES.CAD
              )}`
            : ""
      },
      {
        field: "max_impressions",
        headerName: translate("label.impressions.max"),
        minWidth: 90,
        flex: 2,
        renderCell: params => (params.row.max_impressions ? `${getFormatedNumber(params.row.max_impressions)}` : "")
      },
      {
        field: "start_date",
        headerName: translate("label.startDate"),
        minWidth: 90,
        flex: 2,
        renderCell: params => (params.row.start_date ? dateSVC.format(params.row.start_date) : "")
      },
      {
        field: "end_date",
        headerName: translate("label.endDate"),
        minWidth: 90,
        flex: 2,
        renderCell: params => (params.row.end_date ? dateSVC.format(params.row.end_date) : "")
      }
    ]
    if (canSave) {
      columns.push({
        field: "actions",
        headerName: translate("label.actions"),
        minWidth: 80,
        maxWidth: 80,
        sortable: false,
        flex: 1,
        renderCell: params => <OrderListAction order={params.row} />
      })
    }
    const initialState: GridInitialStateCommunity = {
      pagination: {
        page: pagination.page,
        pageSize: pagination.pageSize
      }
    }
    if (userCanAccessDspDeal) {
      initialState.columns = { columnVisibilityModel: { advertiser_id: false, campaign_count: false } }
    }
    if (pagination.orderBy && pagination.order) {
      initialState.sorting = {
        sortModel: [{ field: pagination.orderBy, sort: pagination.order }]
      }
    }
    return (
      <FlexBox
        id="order-list"
        vertical
        fullWidth
        sx={{
          "position": "relative",
          "pl": ".4rem",
          "pr": ".4rem",
          "backgroundColor": theme => theme.palette.background.paper,
          "transition": "all 400ms ease",
          "& > *": {
            width: "100%"
          },
          "& .MuiDataGrid-root": {
            border: "none"
          },
          "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd)": {
            backgroundColor: theme => theme.palette.background.default
          },
          "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd):hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)"
          },
          "& .MuiDataGrid-cell": {
            fontSize: ".8rem",
            fontWeight: 300
          },
          ...sx
        }}>
        <DataGrid
          rows={filteredOrders}
          columns={columns}
          loading={isLoading || isRefreshing}
          className="order-list"
          onPageSizeChange={pageSize => refreshOrderList({ pagination: { ...pagination, pageSize } })}
          onPageChange={page => refreshOrderList({ pagination: { ...pagination, page } })}
          page={pagination.page}
          rowsPerPageOptions={[10, 25, 50]}
          paginationMode="server"
          sortingMode="server"
          autoHeight
          disableSelectionOnClick
          onSortModelChange={model => {
            const item = model[0]
            if (!item || !item.sort) {
              refreshOrderList({
                pagination: {
                  ...pagination,
                  order: undefined,
                  orderBy: undefined
                }
              })
            } else {
              refreshOrderList({
                pagination: {
                  ...pagination,
                  orderBy: item.field,
                  order: item.sort as any
                }
              })
            }
          }}
          rowCount={rowCount}
          initialState={initialState}
          localeText={getDataGridTexts(hasRefreshError ? "-" : undefined)}
        />
        {hasRefreshError && (
          <FlexBox
            fullWidth
            sx={{
              position: "absolute",
              height: "calc(100% - 3.5rem)",
              top: "3.5rem",
              backgroundColor: theme => alpha(theme.palette.background.paper, 0.4)
            }}>
            <Typography color="error" fontSize="1rem" fontWeight={600}>
              {translate("label.list.error")}
            </Typography>
          </FlexBox>
        )}
        {selectedOrder !== null && <OrderCampaignsPopup order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
        <OrderFormPopup
          onSave={() => {
            closeOrderFormPopup()
            refreshOrderList({})
          }}
          isDspDeal={isDspDeal}
        />
      </FlexBox>
    )
  }
)
